import { DataTable } from '@/components/data-table';
import ContentLayout from '@/components/layout/ContentLayout';
import { Button } from '@/components/ui/button';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CascadingSelect, type CascadingOption } from '@/components/cascading-select';
import { useAuth } from '@/contexts/AuthContext';
import { createClassroom, createQuiz, publishAssignment, getQuestionListByTopicV3, getTopicList, useGetKnowledgePointList, getQuestionInfo } from '@/services/generatedAPIs';
import { useSubjectList } from '@/hooks/use-subject-list';
import { getSubjectDisplayName } from '@/hooks/use-subject-name';
import { QuestionInfo, QuestionInfoExt, QuestionLabel } from '@/services/generatedAPIs.schemas';

import { format } from 'date-fns';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown } from 'lucide-react';
import { installMathFieldWrapper } from '@/components/readonly-question-display';
import React, { useEffect, useState } from 'react';
import { useTranslation, WithTranslation, withTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { AssignmentBasicInfo } from './AssignmentPage';
import { ROUTE } from '@/routes/constants';
import { getQuestionText, getStandardAnswerText } from '@/lib/utils';
import { convertSpacesToLatex } from '@/utils/preprocess';
import { HomeworkPreviewDialog, PreviewMode } from '@/components/homework-preview-dialog';
import { isEN } from '@/utils/i18n';
import { toast } from 'sonner';
import { saveHomeworkDraft, removeHomeworkDraft } from '@/utils/homework-draft-utils';
import { useAsyncAction } from '@/hooks/use-async-action';

type TableData = {
  questionInfo: QuestionInfo,
  questionText: string,
  difficulty: number,
  topics: string[],
  knowledgePoints: string[],
}

const ManualCompileAssignmentPageInner = ({ state }: { state: AssignmentBasicInfo }) => {
  const { getCurrentUser } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
  const { subjectInfos } = useSubjectList();
  const subjectName = getSubjectDisplayName(subjectInfos, state.subjectId);

  // const { data: questionInfoRsp, error } = useGetQuestionListByTopic({
  //   topicIds: state.topicIds,
  //   schoolName: getCurrentUser()!.schoolName,
  //   includePress: true,
  // }, { swr: { swrKey: 'ManualCompileAssignmentPage-getQuestionListByTopic' } });
  // let library = new Array<QuestionInfo>();
  // if (questionInfoRsp?.data?.questionList && questionInfoRsp?.data?.questionList.length > 0 && state.questionInfoList && state.questionInfoList.length > 0) {
  //   for (let i = 0; i < questionInfoRsp.data.questionList.length; ++i) {
  //     const selectedQuestionIds = new Set(state.questionInfoList[i].map((value) => value.id));
  //     library = library.concat(questionInfoRsp.data.questionList[i].filter((value) => !selectedQuestionIds.has(value.id)));
  //   }
  // } else {
  //   library = questionInfoRsp?.data?.questionList.flat() ?? [];
  // }
  let [selectedQuestions, setSelectedQuestions] = useState<QuestionInfoExt[]>(state.questionInfoList?.flat() ?? []);
  let [libraryQuestions, setLibraryQuestions] = useState<QuestionInfoExt[]>([]);
  const [allTopics, setAllTopics] = useState<QuestionLabel[]>([]);
  const [currentTopics, setCurrentTopics] = useState<QuestionLabel[]>([]);
  const [questionTopicMap, setQuestionTopicMap] = useState<Map<number, QuestionLabel[]>>(new Map());
  const [libraryKnowledgePointFilter, setLibraryKnowledgePointFilter] = useState<string[]>([]);
  const [selectedKnowledgePointFilter, setSelectedKnowledgePointFilter] = useState<string[]>([]);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  // Draft ID if this page was resumed from a draft
  const draftId = (state as any)?.draftId;

  // New question ID if coming back from AI question creation
  const newQuestionId = (state as any)?.newQuestionId as number | undefined;

  // 获取知识点数据
  const { data: knowledgePointData, error: knowledgePointError, isLoading: knowledgePointLoading } = useGetKnowledgePointList({
    teacherId: getCurrentUser()!.id,
    schoolName: getCurrentUser()!.schoolName,
    topicId: ''
  });

  // 构建级联选择器的选项数据 - 基于用户之前选中的 Topic 和 KnowledgePoint
  const cascadingOptions = React.useMemo((): CascadingOption[] => {
    if (!allTopics.length || !knowledgePointData?.data?.knowledgePoints) {
      return [];
    }

    // 从全局数据中筛选出用户选中的 Topic
    const selectedTopics = allTopics.filter(topic => state.topicIds.includes(topic.id!));
    
    // 从全局数据中筛选出用户选中的 KnowledgePoint
    const selectedKnowledgePoints = knowledgePointData.data.knowledgePoints.filter(kp => 
      state.knowledgePointIds?.includes(kp.id!)
    );

    return selectedTopics.map(topic => ({
      label: isEN(i18n) ? (topic.enName || topic.name!) : topic.name!,
      value: topic.id!.toString(),
      children: selectedKnowledgePoints
        .filter(kp => kp.mtrId && kp.mtrId.startsWith(topic.mtrId + '-'))
        .map(kp => ({
          label: isEN(i18n) ? (kp.enName || kp.name!) : kp.name!,
          value: kp.id!.toString(),
        }))
    })).filter(option => option.children && option.children.length > 0);
  }, [allTopics, knowledgePointData, state.topicIds, state.knowledgePointIds, i18n]);

  useEffect(() => {
    const loadData = async () => {
      try {
        // 获取所有topic列表
        const topicListRsp = await getTopicList({
          teacherId: getCurrentUser()!.id,
          schoolName: getCurrentUser()!.schoolName
        });

        if (topicListRsp.code === 200 && topicListRsp.data) {
          setAllTopics(topicListRsp.data.topicInfos);

          // 筛选出当前请求中的topics
          const requestTopics = topicListRsp.data.topicInfos.filter(topic =>
            state.topicIds.includes(topic.id!)
          );
          setCurrentTopics(requestTopics);
        }

        // 获取题目列表
        const questionRsp = await getQuestionListByTopicV3({
          topicIds: [],
          knowledgePointIds: state.knowledgePointIds || [],
          libraryNames: state.libraryNames,
          includePress: true,
          language: state.language,
        });

        let library = new Array<any>();
        if (questionRsp?.data?.questionList && questionRsp?.data?.questionList.length > 0 && state.questionInfoList && state.questionInfoList.length > 0) {
          const selectedQuestionIds = new Set(state.questionInfoList.map((value) => value.id));
          library = library.concat(questionRsp.data.questionList.filter((value: QuestionInfoExt) => !selectedQuestionIds.has(value.id)));
        } else {
          library = questionRsp?.data?.questionList.flat() ?? [];
        }

        // 对题目列表进行排序：只把当前用户创建的题目放在前面，其他题目保持原有顺序
        const currentUserId = getCurrentUser()!.email;
        const currentUserQuestions = library.filter((q: QuestionInfoExt) => q.creatorId === currentUserId);
        const otherQuestions = library.filter((q: QuestionInfoExt) => q.creatorId !== currentUserId);
        library = [...currentUserQuestions, ...otherQuestions];

        setLibraryQuestions(library);
        libraryQuestions = library;


      } catch (error) {
        console.log('Load data error', error);
      }
    };

    loadData();
  }, []);

  // Track processed question IDs to prevent duplicate additions
  const processedNewQuestionIds = React.useRef<Set<number>>(new Set());

  useEffect(() => {
    installMathFieldWrapper();
  }, [selectedQuestions, libraryQuestions]);

  // Effect to fetch and add newly created question when coming from AI question creation
  useEffect(() => {
    const fetchNewQuestion = async () => {
      if (!newQuestionId) return;

      // Check if we've already processed this question ID
      if (processedNewQuestionIds.current.has(newQuestionId)) return;

      // Mark as processed immediately to prevent duplicate calls
      processedNewQuestionIds.current.add(newQuestionId);

      try {
        const response = await getQuestionInfo({ questionId: newQuestionId });
        if (response.code === 200 && response.data?.question) {
          const newQuestion = response.data.question;

          // Extract topic and knowledge point info from questionLabelList
          const questionLabels = response.data.questionLabelList || [];
          console.log(questionLabels);
          
          const topicLabels = questionLabels.filter(label => label.type === 'topic');
          const knowledgePointLabels = questionLabels.filter(label => label.type === 'knowledge_point');
          
          const topicIds = topicLabels.map(label => label.id).filter(Boolean) as number[];
          const topicNames = topicLabels.map(label =>
            isEN(i18n) ? (label.enName || label.name) : label.name
          ).filter(Boolean) as string[];
          const knowledgePointIds = knowledgePointLabels.map(label => label.id).filter(Boolean) as number[];
          const knowledgePointNames = knowledgePointLabels.map(label =>
            isEN(i18n) ? (label.enName || label.name) : label.name
          ).filter(Boolean) as string[];

          // Create extended question with full label info
          const extendedQuestion = {
            ...newQuestion,
            topicIds,
            topicNames,
            knowledgePointIds,
            knowledgePointNames,
          };

          // Add to selected questions (check for duplicates in the update function)
          setSelectedQuestions(prev => {
            // Double-check to avoid duplicates
            if (prev.some(q => q.id === newQuestionId)) {
              return prev;
            }
            return [...prev, extendedQuestion];
          });
          // Remove from library questions if present
          setLibraryQuestions(prev => prev.filter(q => q.id !== newQuestionId));
          toast.success(t('common.success'));
        }
      } catch (error) {
        console.error('Failed to fetch new question:', error);
        // Remove from processed set if fetch failed, allowing retry
        processedNewQuestionIds.current.delete(newQuestionId);
      }
    };

    fetchNewQuestion();
  }, [newQuestionId]);

  const onPublish = () => execute(async () => {
    if (!subjectName) {
      toast.error(t('common.loading') || 'Subject data is loading, please wait.');
      return;
    }
    const quizRsp = await createQuiz({
      title: state.title,
      description: state.description,
      subject: subjectName,
      subjectId: state.subjectId,
      originName: getCurrentUser()!.schoolName,
      originType: "school",
      language: state.language,
      teacher_id: getCurrentUser()!.id,
      topicIds: state.topicIds,
      questionInfoIds: selectedQuestions.map(question => question.id!),
    });
    for (const classInfo of state.classInfos) {
      const rsp = await createClassroom({
        classroomType: 0,
        classroomInfoId: classInfo.id,
        teacherId: getCurrentUser()!.id,
        subject: subjectName,
        subjectId: state.subjectId,
      });
      const ret = await publishAssignment({
        title: state.title,
        description: state.description,
        type: "homework",
        teacherId: getCurrentUser()!.id,
        classInfoId: classInfo.id,
        classSessionId: rsp.data!.sessionId,
        quizId: quizRsp.data!.id,
        dueDate: format(state.dueDate, "yyyy-MM-dd'T'HH:mm:ss"),
        topicIds: state.topicIds,
      });
      console.log('publish assignment ', ret, rsp.data!.sessionId);
    }
    alert(t('teacher.publishsuccess'));
    if (draftId) {
      removeHomeworkDraft(draftId);
    }
    navigate(ROUTE.TEACHER.OVERVIEW);
  });

  const handleSaveDraft = () => {
    try {
      // saveHomeworkDraft uses draftId for ID-based update, falls back to hash matching
      saveHomeworkDraft({
        title: state.title || '',
        description: state.description,
        topicIds: state.topicIds || [],
        knowledgePointIds: state.knowledgePointIds,
        libraryNames: state.libraryNames || [],
        subjectId: state.subjectId,
        subjectName: subjectName,
        classInfos: state.classInfos || [],
        dueDate: state.dueDate ? state.dueDate.toISOString() : new Date().toISOString(),
        questionInfoList: selectedQuestions as any,
        language: state.language,
        currentPage: 'manualedit',
      }, draftId);
      toast.success(t('teacher.draftsaved') || 'Draft saved! Will be retained for 24 hours.');
    } catch (error) {
      toast.error(t('common.error'));
    }
  }

  const handleAIGen = () => {
    // Navigate to question creation with AI generation mode
    // Pass the current homework state for resuming after question creation
    const topicId = state.topicIds?.[0];
    const topicName = currentTopics?.[0]?.name || '';

    if (!topicId) {
      toast.error(t('common.error') + ': Missing topic');
      return;
    }

    navigate(ROUTE.TEACHER.LIBRARY_QUESTION_EDIT, {
      state: {
        isEditMode: false,
        topicId: topicId,
        topicName: topicName,
        isAIGenerate: true,
        resumeHomeworkState: {
          ...state,
          questionInfoList: selectedQuestions,
        },
      }
    });
  }

  const onSelect = (selectedInfo: QuestionInfoExt) => {
    const newLibraryQuestions = Array.from(libraryQuestions);
    newLibraryQuestions.splice(libraryQuestions.indexOf(selectedInfo), 1);
    const newSelectedQuestions = selectedQuestions.concat(selectedInfo);
    setLibraryQuestions(newLibraryQuestions);
    setSelectedQuestions(newSelectedQuestions);
  }

  const onUnselect = (selectedInfo: QuestionInfoExt) => {
    const newSelectedQuestions = Array.from(selectedQuestions);
    newSelectedQuestions.splice(selectedQuestions.indexOf(selectedInfo), 1);
    const newLibraryQuestions = libraryQuestions.concat(selectedInfo);
    setSelectedQuestions(newSelectedQuestions);
    setLibraryQuestions(newLibraryQuestions);
  }

  const getQuestionTopics = (questionInfo: QuestionInfoExt): string[] => {
    return questionInfo.topicNames || [];
  }

  const getQuestionKnowledgePoints = (questionInfo: QuestionInfoExt): string[] => {
    return questionInfo.knowledgeNames || [];
  }

  const createTableData = (questionInfo: QuestionInfoExt): TableData => {
    return {
      questionText: getQuestionText(questionInfo, i18n),
      questionInfo: questionInfo,
      difficulty: questionInfo.difficultyLevel || 0,
      topics: getQuestionTopics(questionInfo),
      knowledgePoints: getQuestionKnowledgePoints(questionInfo)
    };
  }

  const getFilteredLibraryQuestions = () => {
    if (libraryKnowledgePointFilter.length === 0) {
      return libraryQuestions;
    }

    return libraryQuestions.filter((question: QuestionInfoExt) => {
      return libraryKnowledgePointFilter.some(filterId => {
        const id = parseInt(filterId);
        if (question.topicIds?.includes(id)) return true;
        if (question.knowledgePointIds?.includes(id)) return true;
        return false;
      });
    });
  }

  const getFilteredSelectedQuestions = () => {
    if (selectedKnowledgePointFilter.length === 0) {
      return selectedQuestions;
    }

    return selectedQuestions.filter((question: QuestionInfoExt) => {
      return selectedKnowledgePointFilter.some(filterId => {
        const id = parseInt(filterId);
        if (question.topicIds?.includes(id)) return true;
        if (question.knowledgePointIds?.includes(id)) return true;
        return false;
      });
    });
  }

  const columns: ColumnDef<TableData>[] = [
    {
      id: "select",
      header: ({ table }) => (
        //   <Checkbox
        //     checked={
        //       table.getIsAllPageRowsSelected() ||
        //       (table.getIsSomePageRowsSelected() && "indeterminate")
        //     }
        //     onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        //     aria-label="Select all"
        //   />
        <></>
      ),
      cell: ({ row }) => (
        //   <Checkbox
        //     checked={row.getIsSelected()}
        //     onCheckedChange={(value) => row.toggleSelected(!!value)}
        //     aria-label="Select row"
        //   />
        <></>
      ),
      enableSorting: false,
      enableHiding: false,
    },

    {
      accessorKey: "standardAnswer",
      header: () => { return (<span>{t("common.answer")}</span>) },
      cell: ({ row }) => {
        return (
          <math-field read-only className="w-3xs break-all border-gray-200">
            {convertSpacesToLatex(getStandardAnswerText(row.original.questionInfo, i18n))}
          </math-field>
        )
      },
    },
    {
      accessorKey: "type",
      header: () => { return (<span>{t("teacher.qtype")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.questionInfo.questionType}</div>
      ),
    },
  ];

  const selectedColumn = Array.from(columns);
  selectedColumn.splice(1, 0, {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const info = row.original.questionInfo;

      return (
        <div className='flex place-content-evenly'>
          <Button variant="outline" onClick={() => { onUnselect(info); }}>{t('teacher.delete')}</Button>
        </div>
      )
    },
  },
    {
      accessorKey: "questionText",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("teacher.choose") + t("common.question")}
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => {
        return (
          <math-field read-only className="w-3xs break-all border-gray-200">
            {convertSpacesToLatex(getQuestionText(row.original.questionInfo, i18n, state.language))}
          </math-field>
        )
      },
    },
    {
      accessorKey: "difficulty",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("common.difficulty")}
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => (
        <div>{row.original.difficulty}</div>
      ),
    },
    {
      accessorKey: "topics",
      header: () => { return (<span>{t("common.topic")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.topics.join(', ')}</div>
      ),
    },
    {
      accessorKey: "knowledgePoints",
      header: () => { return (<span>{t("common.knowledgepoint")}</span>) },
      cell: ({ row }) => (
        <div className="whitespace-normal break-keep max-w-xs">{row.original.knowledgePoints.join(', ')}</div>
      ),
    });

  const libraryColumn = Array.from(columns);
  libraryColumn.splice(1, 0, {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const info = row.original.questionInfo;

      return (
        <div className='flex place-content-evenly'>
          <Button variant="outline" onClick={() => { onSelect(info); }}>{t("teacher.choose")}</Button>
        </div>
      )
    },
  }, {
    accessorKey: "questionText",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {t("common.question")}
          <ArrowUpDown />
        </Button>
      )
    },
    cell: ({ row }) => {
      return (
        <math-field read-only className="w-3xs break-all border-gray-200">
          {convertSpacesToLatex(getQuestionText(row.original.questionInfo, i18n, state.language))}
        </math-field>
      )
    },
  },
    {
      accessorKey: "difficulty",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("common.difficulty")}
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => (
        <div>{row.original.difficulty}</div>
      ),
    },
    {
      accessorKey: "topics",
      header: () => { return (<span>{t("common.topic")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.topics.join(', ')}</div>
      ),
    },
    {
      accessorKey: "knowledgePoints",
      header: () => { return (<span>{t("common.knowledgepoint")}</span>) },
      cell: ({ row }) => (
        <div className="whitespace-normal break-keep max-w-xs">{row.original.knowledgePoints.join(', ')}</div>
      ),
    });

  return (
    <ContentLayout title={t('teacher.manualedit')}>
      <LoadingOverlay isLoading={isLoading} text={t('common.submitting')} />
      {/* <Label>topic:</Label> */}
      <div className='w-full flex flex-row bg-background'>
        <div className='shrink w-1/2 p-2'>
          <DataTable
            data={getFilteredLibraryQuestions().map(createTableData)}
            columns={libraryColumn}
            filterColumn='questionText'
            filterName={t('common.question')}
            renderButton={() => (
              <CascadingSelect
                className="w-[200px]"
                options={cascadingOptions}
                value={libraryKnowledgePointFilter}
                onChange={setLibraryKnowledgePointFilter}
                placeholder={t('component.selectknowledgepoint')}
              />
            )}
          />
        </div>

        <div className='shrink w-1/2 p-2'>
          <DataTable
            data={getFilteredSelectedQuestions().map(createTableData)}
            columns={selectedColumn}
            filterColumn='questionText'
            filterName={t('common.question')}
            renderButton={() => (
              <CascadingSelect
                className="w-[200px]"
                options={cascadingOptions}
                value={selectedKnowledgePointFilter}
                onChange={setSelectedKnowledgePointFilter}
                placeholder={t('component.selectknowledgepoint')}
              />
            )}
          />
        </div>
      </div>

      <div className='flex place-content-evenly pt-2'>
        <Button variant="secondary" onClick={() => { navigate(-1); }}>{t('common.goprev')}</Button>
        <Button variant="secondary" onClick={() => {
          navigate(ROUTE.TEACHER.LIBRARY_EDIT)
        }}>{t("teacher.goedit")}</Button>
        <Button variant="default" className="bg-chart-4 hover:bg-chart-4/70" onClick={() => { handleAIGen(); }}>{t('teacher.aigennow')}</Button>
        <Button variant="secondary" onClick={() => { handleSaveDraft(); }}>{t('teacher.savedraft')}</Button>
        <Button variant="default" className='bg-primary' onClick={() => {
          if (selectedQuestions.length === 0) {
            alert(t('teacher.alert'));
            return;
          }
          setShowPreviewDialog(true);
        }}>{t('teacher.publish')}</Button>
      </div>

      {/* 预览弹窗 */}
      <HomeworkPreviewDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        questions={selectedQuestions}
        mode={PreviewMode.QUIZ}
        title={state.title}
        description={state.description}
        subject={subjectName}
        unit={currentTopics.map(topic => topic.name).join(', ')}
        className={state.classInfos.map(classInfo => classInfo.name).join(', ')}
        dueDate={state.dueDate}
        onConfirm={onPublish}
        isLoading={isLoading}
        language={state.language}
      />
    </ContentLayout>
  );
};

const ManualCompileAssignmentPage = () => {
  const state = useLocation().state as AssignmentBasicInfo | null;
  const navigate = useNavigate();

  const isValidState = (s: any): s is AssignmentBasicInfo => {
    return !!s && Array.isArray(s.topicIds) && Array.isArray(s.classInfos) && !!s.dueDate && !!s.language;
  };

  useEffect(() => {
    if (!isValidState(state)) {
      navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
    }
  }, [state, navigate]);

  if (!isValidState(state)) {
    return <LoadingOverlay />;
  }

  return <ManualCompileAssignmentPageInner state={state} />;
};

export default ManualCompileAssignmentPage;